// Signup — create an account: register the user, trigger the registration
// OTP, then hand off to the shared verify screen (otpType=register).

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import {
  Button,
  Field,
  Input,
  PasswordInput,
  PasswordStrength,
  PhoneInput,
} from '@/components/common';
import { countries, defaultCountry } from '@/mocks/countries';
import AuthLayout from '@/layout/AuthLayout';
import { OTPType, useRegisterMutation, useSendOtpMutation } from '@/api/endpoints/authApi';
import type { ApiErrorShape } from '@/api/types';
import useOtpCooldown from '@/hooks/useOtpCooldown';
import { phoneOtpKey } from '@/redux/otp/otpKeys';
import { scorePassword } from '@/utils/password';
import AuthBack from './AuthBack';
import { validateAll, validateField, type FormErrors } from './validation';

const schema = yup.object({
  firstName: yup.string().trim().required('error_first_name_required'),
  lastName: yup.string().trim().required('error_last_name_required'),
  email: yup.string().email('error_invalid_email'),
  phone: yup
    .string()
    .required('error_phone_required')
    .matches(/^\d{7,12}$/, { message: 'error_invalid_phone_number', excludeEmptyString: true }),
  password: yup
    .string()
    .required('error_password_required')
    .test('strength', 'error_password_weak', (value) => scorePassword(value ?? '') >= 3),
});

interface SignupValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
}

const initialValues: SignupValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  password: '',
};

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [register, { isLoading: registering }] = useRegisterMutation();
  const [sendOtp, { isLoading: sending }] = useSendOtpMutation();
  const submitting = registering || sending;

  const [dialCode, setDialCode] = useState(defaultCountry.dial);
  const [values, setValues] = useState<SignupValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  // No button gate here — a duplicate register() fails before send-otp is ever
  // reached — but if this number's REGISTER code is still cooling (e.g. after a
  // back-navigation), skip the redundant send and go straight to verification.
  const cooldown = useOtpCooldown(
    values.phone ? phoneOtpKey(OTPType.REGISTER, dialCode, values.phone) : null,
  );

  const setValue = <K extends keyof SignupValues>(name: K, value: SignupValues[K]) => {
    const next = { ...values, [name]: value };
    setValues(next);
    if (submitted) {
      validateField(schema, name, next).then((message) =>
        setErrors((prev) => ({ ...prev, [name]: message })),
      );
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const nextErrors = await validateAll(schema, values);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    try {
      await register({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email || undefined,
        password: values.password,
        countryCode: dialCode,
        mobileNumber: values.phone,
      }).unwrap();

      // Send the registration OTP, then move to verification.
      // (If the backend auto-sends on register, drop this call.)
      if (!cooldown.active) {
        await sendOtp({
          countryCode: dialCode,
          mobileNumber: values.phone,
          otpType: OTPType.REGISTER,
        }).unwrap();
      }

      toast.success(t('toast_account_created'));
      navigate('/verify-otp', {
        state: { otpType: OTPType.REGISTER, dialCode, phone: values.phone },
      });
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <AuthLayout variant='signup'>
      <AuthBack onClick={() => navigate('/login')} />

      <div className='auth-page'>
        <header className='auth-page__header'>
          <h1 className='auth-page__title'>{t('text_welcome_to_midani')}</h1>
          <p className='auth-page__subtitle'>{t('text_create_account')}</p>
        </header>

        <form className='auth-page__form' onSubmit={handleSubmit} noValidate>
          <Field
            label={t('field_first_name')}
            htmlFor='signup-first-name'
            error={errors.firstName ? t(errors.firstName) : undefined}
          >
            <Input
              id='signup-first-name'
              placeholder={t('field_your_first_name')}
              autoComplete='given-name'
              value={values.firstName}
              onChange={(e) => setValue('firstName', e.target.value)}
              error={!!errors.firstName}
            />
          </Field>

          <Field
            label={t('field_last_name')}
            htmlFor='signup-last-name'
            error={errors.lastName ? t(errors.lastName) : undefined}
          >
            <Input
              id='signup-last-name'
              placeholder={t('field_your_last_name')}
              autoComplete='family-name'
              value={values.lastName}
              onChange={(e) => setValue('lastName', e.target.value)}
              error={!!errors.lastName}
            />
          </Field>

          <Field
            label={t('field_email_optional')}
            htmlFor='signup-email'
            error={errors.email ? t(errors.email) : undefined}
          >
            <Input
              id='signup-email'
              type='email'
              iconEnd='mail'
              placeholder={t('field_your_email_address')}
              autoComplete='email'
              value={values.email}
              onChange={(e) => setValue('email', e.target.value)}
              error={!!errors.email}
            />
          </Field>

          <Field
            label={t('field_phone_number')}
            htmlFor='signup-phone'
            error={errors.phone ? t(errors.phone) : undefined}
          >
            <PhoneInput
              id='signup-phone'
              countries={countries}
              dialCode={dialCode}
              onDialCodeChange={setDialCode}
              value={values.phone}
              onChange={(digits) => setValue('phone', digits)}
              error={!!errors.phone}
            />
          </Field>

          <Field
            label={t('field_password')}
            htmlFor='signup-password'
            error={errors.password ? t(errors.password) : undefined}
          >
            <PasswordInput
              id='signup-password'
              placeholder={t('field_enter_password')}
              autoComplete='new-password'
              value={values.password}
              onChange={(e) => setValue('password', e.target.value)}
              error={!!errors.password}
            />
            <PasswordStrength password={values.password} />
          </Field>

          <Button type='submit' size='lg' loading={submitting} className='auth-page__submit'>
            {t('button_create_account')}
          </Button>

          <p className='auth-page__alt'>
            {t('text_already_have_an_account')}{' '}
            <button type='button' className='auth-page__link' onClick={() => navigate('/login')}>
              {t('text_login_here')}
            </button>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Signup;
